import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faXmark } from '@fortawesome/free-solid-svg-icons';
import './BarcodeCameraButton.scss';

// Camera barcode scan via the browser's built-in BarcodeDetector (Chrome/Edge on Android,
// recent desktop Chromium) — no extra library. Where the API doesn't exist (Safari, Firefox)
// the button simply isn't rendered and the barcode is typed in instead.
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'data_matrix'];
export const isBarcodeCameraSupported = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;

const BarcodeCameraButton = ({ onDetected, label = 'Scanner', className = '' }) => {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        let stream;
        let stopped = false;
        let timer;
        (async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                if (stopped) return;
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                const supported = await window.BarcodeDetector.getSupportedFormats();
                const detector = new window.BarcodeDetector({ formats: FORMATS.filter(f => supported.includes(f)) });
                const tick = async () => {
                    if (stopped) return;
                    try {
                        const codes = await detector.detect(videoRef.current);
                        const value = codes[0]?.rawValue;
                        if (value) { onDetected(value); setOpen(false); return; }
                    } catch { /* frame not ready */ }
                    timer = setTimeout(tick, 250);
                };
                tick();
            } catch (e) {
                setError("Impossible d'accéder à la caméra.");
            }
        })();
        return () => {
            stopped = true;
            clearTimeout(timer);
            stream?.getTracks().forEach(t => t.stop());
        };
    }, [open, onDetected]);

    if (!isBarcodeCameraSupported()) return null;

    return (
        <>
            <button type="button" className={`bcam-btn ${className}`} onClick={() => { setError(null); setOpen(true); }} aria-label={label} title={label}>
                <FontAwesomeIcon icon={faCamera} />
            </button>
            {open && (
                <div className="bcam-overlay" role="dialog" aria-modal="true">
                    <div className="bcam-box">
                        <button type="button" className="bcam-close" onClick={() => setOpen(false)} aria-label="Fermer">
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                        {error ? (
                            <p className="bcam-error">{error}</p>
                        ) : (
                            <>
                                <video ref={videoRef} className="bcam-video" muted playsInline />
                                <div className="bcam-frame" />
                                <p className="bcam-hint">Placez le code-barres dans le cadre</p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default BarcodeCameraButton;
